import { Box, Stack, useTheme } from "@mui/material";

import { createLogger } from "@/logger";

const log = createLogger("bootstrap");

import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Loading from "@/components/Loading/Index";
import { fetchFeedItemsAndDownloadEvents } from "@/models/actions/bootstrap";
import { LiveQueryContext } from "@/models/LiveQueriesProvider";
import NoChannelsAvailable from "@/routes/workspace/NoChannelsAvailable";
import { runBootTask } from "@/store/bootRun";
import { store } from "@/store/store";

export default function LoadingWorkspace() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { myActiveFeeds } = useContext(LiveQueryContext);
  const params = useParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [noChannelsAvailable, setNoChannelsAvailable] = useState<boolean>(false);

  useEffect(() => {
    log.debug("Loading Workspace", { loading, myActiveFeeds });
    if (myActiveFeeds?.length === 0) {
      const timer = setTimeout(() => {
        setNoChannelsAvailable(true);
      }, 250);
      return () => clearTimeout(timer);
    }

    if (myActiveFeeds?.length > 0 && !loading) {
      setNoChannelsAvailable(false);
      setLoading(() => true);
      const firstFeed = myActiveFeeds[0];
      const feedId = firstFeed.id;
      const workspaceId = params.workspaceId;
      let cancelled = false;
      // The first feed's fetch counts toward the rail indicator while the workspace is still booting; after that it
      // passes through.
      runBootTask("warming", "route.loadingWorkspace", fetchFeedItemsAndDownloadEvents(feedId, workspaceId)).then(
        async (itemCount) => {
          if (cancelled) return;
          if (itemCount > 0) {
            store.update("feed", feedId, (row) => (row.loadedLastPage ? row : { ...row, loadedLastPage: true }));
          }
          navigate(
            firstFeed.aliasMembershipId
              ? `/workspaces/${workspaceId}/drivers/${firstFeed.aliasMembershipId}/channel/${feedId}`
              : `/workspaces/${workspaceId}/channels/${feedId}`,
          );
          setLoading(() => false);
        },
      );
      return () => {
        cancelled = true;
      };
    }
  }, [myActiveFeeds, params?.workspaceId]);

  if (noChannelsAvailable) {
    return <NoChannelsAvailable />;
  }

  return (
    loading && (
      <>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            height: "100%",
          }}
          data-feed-layout-wrap
        >
          <Stack
            sx={{
              mx: "auto",
              display: "flex",
              justifyContent: "center",
              my: 6,
              maxWidth: "600px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                textAlign: "center",
                gap: "28px",
                width: "100%",
                maxWidth: { xs: "calc(100% - 32px)", sm: "415px" },
                padding: 2,
                borderRadius: "8px",
                background: theme.palette.secondary.dark,
              }}
            >
              <Loading size={"small"} />
            </Box>
          </Stack>
        </Box>
      </>
    )
  );
}
